import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TOOL_KEYS = [
  'eventi',
  'servizi',
  'barcode',
  'cassa',
  'inventario',
  'posizione_temporanea',
];

const DEFAULTS = TOOL_KEYS.reduce((acc, k) => ({ ...acc, [k]: false }), {});

/**
 * Central hook for the shop's modular tools (v1.8.0).
 *
 * Every page that shows/hides features based on a Strumento must
 * derive its state from here, never from ad-hoc checks. Values are
 * always booleans, and unknown keys default to `false`.
 *
 * Usage:
 *   const { tools, isEnabled, refresh, update } = useShopTools();
 *   if (isEnabled('inventario')) { ... }
 */
export default function useShopTools() {
  const [tools, setTools] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/shops/my/tools`, { withCredentials: true });
      setTools({ ...DEFAULTS, ...(data?.tools || {}) });
    } catch (e) {
      // Not fatal — the user might not have a shop yet.
      if (e?.response?.status !== 404) {
        setError(e);
      }
      setTools(DEFAULTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const update = useCallback(async (patch) => {
    const { data } = await axios.put(`${API}/shops/my/tools`, patch, {
      withCredentials: true,
    });
    setTools({ ...DEFAULTS, ...(data?.tools || {}) });
    return data;
  }, []);

  const isEnabled = useCallback((key) => !!tools[key], [tools]);

  return { tools, loading, error, isEnabled, refresh: fetchTools, update };
}
